/** Any template token, in one pass. Tag alternatives come first so one is never read as a variable. */
const TOKEN_PATTERN =
  /\{\{(#if_|else_|\/if_|#block:|\/block:)([\w-]+)\}\}|(\{\{\s*[\w.-]+\s*\}\})/g

type Region = { kind: "if" | "block"; name: string }

const BODY_CLASS: Record<Region["kind"], string> = {
  if: "pv-if-body",
  block: "pv-block-body"
}

export type TemplatePiece = { text: string; className?: string }

/**
 * Splits prompt text into coloured pieces, marking tags AND the region they gate so it is
 * visible that the body disappears when the flag is off.
 *
 * The scanner keeps its open-region state between calls, because in the rendered preview one
 * region spans several text nodes (the tag in one paragraph, the rule it gates in the next).
 * Tolerant of half-typed and unbalanced tags — this runs on every keystroke.
 */
export const createTemplateScanner = () => {
  const open: Region[] = []
  const bodyClass = () => {
    const innermost = open[open.length - 1]
    return innermost ? BODY_CLASS[innermost.kind] : undefined
  }

  return (text: string): TemplatePiece[] => {
    const pieces: TemplatePiece[] = []
    const push = (value: string, className?: string) => {
      if (value) pieces.push({ text: value, className })
    }

    let cursor = 0
    const pattern = new RegExp(TOKEN_PATTERN.source, "g")
    let match = pattern.exec(text)

    while (match) {
      push(text.slice(cursor, match.index), bodyClass())

      const [token, tagKind, tagName, variable] = match
      if (variable) {
        push(token, "pv-variable")
      } else {
        const kind: Region["kind"] = tagKind.includes("block") ? "block" : "if"
        const isClose = tagKind === "/if_" || tagKind === "/block:"
        push(token, `pv-${kind}-tag`)
        // A closing tag belongs to the region it ends, so pop only after it is emitted. An
        // unmatched close is left alone rather than collapsing an unrelated region.
        if (isClose) {
          if (open[open.length - 1]?.name === tagName) open.pop()
        } else if (tagKind !== "else_") {
          open.push({ kind, name: tagName })
        }
      }

      cursor = match.index + token.length
      match = pattern.exec(text)
    }

    push(text.slice(cursor), bodyClass())
    return pieces
  }
}

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

/** For the input side, which repaints the editor's own "pre" mirror layer as an HTML string. */
export const highlightPromptTemplate = (text: string): string =>
  createTemplateScanner()(text)
    .map(({ text: value, className }) =>
      className
        ? `<span class="${className}">${escapeHtml(value)}</span>`
        : escapeHtml(value)
    )
    .join("")
